import type { TypedDocumentNode } from '@graphql-typed-document-node/core'
import { GraphQLClient, RequestDocument, Variables } from 'graphql-request'

import { getAccessTokenId } from 'shared/auth'
import { STRAWBERRY_GRAPHQL_URL } from 'shared/config'

const strawberryClient = new GraphQLClient(STRAWBERRY_GRAPHQL_URL, {
  requestMiddleware: async (request) => {
    const token = await getAccessTokenId()

    if (token) {
      return {
        ...request,
        headers: {
          ...request.headers,
          Authorization: `Bearer ${token}`,
        },
      }
    }

    return request
  },
})

export default function gqlRequest<T, V extends Variables = Variables>(
  document: RequestDocument | TypedDocumentNode<T, V>,
  variables?: V,
  requestHeaders?: HeadersInit,
): Promise<T> {
  // graphql-request's own `request()` overload has a fussy variadic signature (its exact
  // required/optional shape depends on whether V has any required keys) that doesn't unify
  // cleanly with this wrapper's simpler, uniformly-optional public signature above -- the cast
  // is internal only, callers of `gqlRequest` itself stay fully typed. `request` is a regular
  // (unbound) method that reads `this.requestConfig` internally, so it must stay bound to
  // `strawberryClient` -- extracting it as a bare reference and calling it standalone loses
  // `this` and throws "Cannot read properties of undefined (reading 'requestConfig')".
  const request = strawberryClient.request.bind(strawberryClient) as (
    document: RequestDocument | TypedDocumentNode<T, V>,
    variables?: V,
    requestHeaders?: HeadersInit,
  ) => Promise<T>

  return request(document, variables, requestHeaders)
}
